import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Send, Upload, Trash2, Activity, Camera, Save, Zap } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import MarkdownMessage from '../components/MarkdownMessage';
import ConsumablesPiles from '../components/ConsumablesPiles';

export default function Chat() {
    const location = useLocation();
    const navigate = useNavigate();
    const [messages, setMessages] = useState<any[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [vehicleContext, setVehicleContext] = useState<any>(null);
    const [projectId, setProjectId] = useState<string | null>(null);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const { user, isLimitReached, incrementQuestions, login } = useAuth();

    useEffect(() => {
        const state = location.state as any;
        if (state?.messages && state.messages.length > 0) {
            setMessages(state.messages);
            if (state.resumeProject?.id) setProjectId(state.resumeProject.id);

            if (state.initialPrompt) {
                setVehicleContext((prev: any) => ({ ...prev, notes: state.initialPrompt }));
            }
        } else if (state?.initialPrompt) {
            handleSend(state.initialPrompt);
        } else if (state?.resumeProject) {
            setMessages(state.resumeProject.data.messages || []);
            setVehicleContext(state.resumeProject.vehicleContext || null);
            setProjectId(state.resumeProject.id);
        }
    }, [location.state]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (text: string = input) => {
        if (!text.trim()) return;
        if (isLimitReached) return;

        const userMsg = { role: 'user', content: text };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setLoading(true);

        try {
            const data = await api.chat(text, vehicleContext || {}, messages);

            // Extract vehicle context if returned by backend
            if (data.technical_data?.vehicle_info || data.vehicle_context) {
                setVehicleContext(data.technical_data?.vehicle_info || data.vehicle_context);
            }

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: data.response || data.message || 'Processing successful, but no detail returned.',
                difficulty: data.difficulty_level || data.difficulty
            } as any]);
            incrementQuestions();
        } catch (error: any) {
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `### Connection Error\nBackend unreachable. Details: ${error.message || 'Unknown error'}. Please verify the API status or your network connection.`
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploadLoading(true);
        const formDataMsg = { role: 'user', content: '[Analyzing Uploaded Resource...]' };
        setMessages(prev => [...prev, formDataMsg]);

        try {
            const data = await api.analyzeImage(file, 'Manual identification check');
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `**Visual Analysis Result:**\n\n${data.summary || 'Image processed successfully.'}`
            }]);
        } catch (e: any) {
            alert(`Search failed: ${e.message}`);
        } finally {
            setUploadLoading(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col h-[calc(100vh-64px)] overflow-hidden">
            {/* Maintenance Specs Strip */}
            <div className="bg-dark/80 backdrop-blur-md border-b border-white/5 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <ConsumablesPiles vehicleContext={vehicleContext} />
                </div>
            </div>

            {/* Paywall Overlay */}
            {isLimitReached && (
                <div className="absolute inset-x-0 bottom-[100px] top-[140px] z-[40] bg-dark/60 backdrop-blur-md flex items-center justify-center p-6 text-center animate-fade-in">
                    <div className="max-w-md bg-dark border border-primary/30 rounded-3xl p-8 shadow-2xl space-y-6">
                        <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto">
                            <Zap size={32} className="text-primary" />
                        </div>
                        <div className="space-y-2">
                            <h2 className="text-2xl font-black text-white uppercase tracking-tighter">Usage Limit Reached</h2>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                You've experienced the power of DIY-MECH AI for 3 free sessions.
                                Upgrade to **Unlimited Pro** for master-tier diagnostic technical data.
                            </p>
                        </div>
                        <button
                            onClick={() => navigate('/upgrade')}
                            className="w-full bg-gradient-to-r from-primary to-secondary p-4 rounded-xl text-dark font-black tracking-widest uppercase hover:scale-105 active:scale-95 transition-all"
                        >
                            Upgrade to Pro
                        </button>
                    </div>
                </div>
            )}

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {messages.length === 0 && (
                    <div className="h-full flex flex-col items-center justify-center opacity-30 text-center space-y-4">
                        <Activity size={48} className="animate-pulse" />
                        <div>
                            <p className="font-black uppercase tracking-[0.3em]">Quantum Core Ready</p>
                            <p className="text-sm">Initiate diagnostic sequence via command input below.</p>
                        </div>
                    </div>
                )}
                {messages.map((msg, i) => (
                    <div key={i} className="max-w-4xl mx-auto shadow-sm space-y-2">
                        {(msg as any).difficulty && msg.role === 'assistant' && (
                            <div className="flex justify-start">
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded bg-slate/10 border ${(msg as any).difficulty >= 3 ? 'text-red-400 border-red-400/30' :
                                    (msg as any).difficulty === 2 ? 'text-yellow-400 border-yellow-400/30' :
                                        'text-green-400 border-green-400/30'
                                    }`}>
                                    Job Level: {(msg as any).difficulty}
                                </span>
                            </div>
                        )}
                        <MarkdownMessage message={msg} />
                    </div>
                ))}
                {loading && (
                    <div className="max-w-4xl mx-auto flex justify-start">
                        <div className="bg-primary/10 border border-primary/20 rounded-2xl p-4 flex gap-2">
                            {[0, 150, 300].map(d => (
                                <div key={d} className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                            ))}
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Chat Bar Area */}
            <div className="border-t border-slate/20 bg-dark/50 backdrop-blur-xl p-6">
                <div className="max-w-4xl mx-auto space-y-2">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { if (confirm('Clear current thread?')) setMessages([]) }}
                            title="Clear Thread"
                            className="p-3 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-red-400 hover:border-red-400/50 transition-all"
                        >
                            <Trash2 size={20} />
                        </button>

                        <label className="p-3 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-primary hover:border-primary/50 cursor-pointer transition-all">
                            <Upload size={20} className={uploadLoading ? 'animate-spin' : ''} />
                            <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                        </label>

                        <button
                            onClick={async () => {
                                if (!user) {
                                    const email = prompt('Sign in to save projects. Enter your email:');
                                    if (email) login(email);
                                    return;
                                }
                                if (messages.length === 0) return;

                                const currentId = projectId || Date.now().toString();
                                const name = prompt('Enter a name for this project:', vehicleContext ? `${vehicleContext.year} ${vehicleContext.make} ${vehicleContext.model}` : messages[0].content.slice(0, 30));

                                if (name) {
                                    const finalId = await api.saveProject({
                                        id: currentId,
                                        name,
                                        type: 'diagnostic',
                                        data: { messages },
                                        vehicleContext
                                    });
                                    setProjectId(finalId);
                                    alert('Project saved locally.');
                                }
                            }}
                            disabled={messages.length === 0}
                            title="Save Project"
                            className="p-3 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-primary hover:border-primary/50 transition-all disabled:opacity-30"
                        >
                            <Save size={20} />
                        </button>

                        <button
                            onClick={() => navigate('/ar')}
                            title="Spatial Diagnosis"
                            className="p-3 bg-dark border border-slate/30 rounded-xl text-gray-500 hover:text-secondary hover:border-secondary/50 transition-all"
                        >
                            <Camera size={20} />
                        </button>

                        <div className="flex-1 relative group">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                placeholder={isLimitReached ? "Upgrade to continue..." : "Ask about diagnostics, parts, or system errors..."}
                                disabled={isLimitReached}
                                className="w-full bg-dark border border-slate/30 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-primary/50 focus:border-primary focus:outline-none transition-all disabled:opacity-20"
                            />
                            <button
                                onClick={() => handleSend()}
                                disabled={!input.trim() || loading || isLimitReached}
                                className="absolute right-2 top-2 bottom-2 bg-primary px-4 rounded-lg text-dark hover:scale-105 active:scale-95 transition-all disabled:opacity-30"
                            >
                                <Send size={18} />
                            </button>
                        </div>
                    </div>
                    <p className="text-[9px] uppercase tracking-widest text-center text-gray-600 font-bold">
                        Encryption Layer Active • Secured Logic Pipeline
                    </p>
                </div>
            </div>
        </div>
    );
}
